import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { environment } from 'environments/environment';
import { interval } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable()
export class SwUpdateNotifierService {
	private readonly _swUpdate = inject(SwUpdate);
	private readonly _matSnackBar = inject(MatSnackBar);
	private readonly _destroyRef = inject(DestroyRef);
	private readonly _CHECK_INTERVAL = 60 * 1000; // 1 minuto

	init(): void {
		if (!this._swUpdate.isEnabled) return;

		// Escuchar nuevas versiones listas para activar
		this._swUpdate.versionUpdates
			.pipe(
				takeUntilDestroyed(this._destroyRef),
				filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
			)
			.subscribe(() => this._showUpdateNotification());

		if (environment.enableServiceWorker) {
			this._startPeriodicCheck();
		}

		// Chequeo inicial inmediato al cargar
		void this._swUpdate.checkForUpdate();
	}

	private _startPeriodicCheck(): void {
		interval(this._CHECK_INTERVAL)
			.pipe(takeUntilDestroyed(this._destroyRef))
			.subscribe(() => {
				void this._swUpdate.checkForUpdate();
			});
	}

	private _showUpdateNotification(): void {
		const ref = this._matSnackBar.open('Actualización disponible', 'Actualizar', {
			duration: 0, // No se cierra automáticamente
			panelClass: 'update-snackbar',
		});

		ref.onAction().subscribe(() => {
			void this._applyUpdate();
		});
	}

	private async _applyUpdate(): Promise<void> {
		try {
			await this._clearAllCaches();
			await this._swUpdate.activateUpdate();
			document.location.reload();
		} catch (error) {
			document.location.reload(); // Fallback
		}
	}

	private async _clearAllCaches(): Promise<void> {
		try {
			if (!('caches' in window)) return;
			const cacheNames = await caches.keys();
			await Promise.all(
				cacheNames
					.filter((cacheName) => !cacheName.startsWith('ngsw:'))
					.map((cacheName) => caches.delete(cacheName))
			);
		} catch (error) { }
	}
}
